import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { CircularProgress, Modal, Tooltip, Paper, Tab, Tabs, Box, LinearProgress, Snackbar, Alert, IconButton, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { Close, Edit, Check, ArrowForward, SwapHoriz, Assignment, Refresh } from '@mui/icons-material';
import Select from 'react-select';
import { twMerge } from 'tailwind-merge';
import React from 'react';
import { mergeLockSets } from '../../utils/taskLockingHelper';
import TaskDetailsModal from '../../components/TaskDetailsModal/TaskDetailsModal';
import PrecheckedDrivers from './PrecheckedDrivers';
import { 
  saveFacilityToStorage, 
  loadFacilityFromStorage, 
  formatFacilityOptions
} from '../../utils/facilityStorage';
import { formatDirection, formatShipmentType } from '../../utils/shipmentHelpers';

import { getAuthToken } from '../../utils/cookieUtils';
// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Helper function to format dates in military time without timezone conversion
const formatMilitaryDateTime = (dateString: string | Date): string => {
  const dateStr = typeof dateString === 'string' ? dateString : dateString.toISOString();
  
  // Parse the date string directly without timezone conversion
  // Remove timezone offset (e.g., +00:00, -05:00, +0000, -0500, Z) from the end
  const cleanDateStr = dateStr.replace(/([+-]\d{2}:\d{2}|[+-]\d{4}|Z)$/, '');
  
  // Split into date and time parts - handle both 'T' and space separators
  const [datePart, timePart] = cleanDateStr.includes('T') 
    ? cleanDateStr.split('T') 
    : cleanDateStr.split(' ');
  
  if (!datePart || !timePart) return dateStr;
  
  // Format date as MM/DD/YYYY
  const [year, month, day] = datePart.split('-');
  const formattedDate = `${month}/${day}/${year}`;
  
  // Extract hours and minutes for military time (HH:MM)
  // Handle microseconds by splitting on '.' first
  const timeOnly = timePart.split('.')[0];
  const [hours, minutes] = timeOnly.split(':');
  const militaryTime = `${hours}:${minutes}`;
  
  return `${formattedDate} ${militaryTime}`;
};

// Types for slots
type SlotType = 'dock' | 'yard';
type SlotStatus = 'available' | 'in-use' | 'unavailable' | 'scheduled';

interface Slot {
  id: string;
  number: string;
  type: SlotType;
  status: SlotStatus;
  assignedDriverId?: string | null;
  zoneId?: string;
  zoneName?: string;
  scheduledTimes?: {
    start: string;
    end: string;
    driverId?: string;
  }[];
}

// Add interface for zones
interface Zone {
  id: string;
  name: string;
  color: string;
  spots: Slot[];
}

// Interface for facility
interface Facility {
  id: number;
  name: string;
}

// Types for drivers
interface Driver {
  id: string;
  assignmentId: string; // Add separate field for assignment ID (for deep linking)
  driverId: string; // Actual driver ID (not assignment ID)
  name: string;
  loadNumber?: string;
  direction: string;
  type: string;
  sealNumber?: string;
  truckNumber: string;
  trailerNumber?: string;
  mdt_time?: string;
  appointment_date?: string;
  weight?: string;
  arrivalTime: string;
  checkInTime?: string; // Raw check-in timestamp for sorting
  bol_id: string;
  shipment_id: string;
  // Related data from GraphQL
  driver?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  shipment?: {
    id: string;
    referenceNumber: string;
    direction?: string;
    metadata?: any;
    trailer?: {
      uuid: string;
      trailer_number?: string;
      trailer_type?: string;
      refrigerated?: boolean;
      length_feet?: number;
      weight_capacity?: number;
      volume_capacity?: number;
      carrier?: {
        id: string;
        name: string;
      };
    };
  };
}

// Main component implementation
const DriverSlotAssignment = () => {
  // State for facilities
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [selectedFacility, setSelectedFacilityState] = useState<Facility | null>(() => {
    // Initialize from localStorage on mount
    return loadFacilityFromStorage();
  });

  // Wrapper function to save facility selection to localStorage
  const setSelectedFacility = (facility: Facility | null) => {
    setSelectedFacilityState(facility);
    saveFacilityToStorage(facility);
  };
  
  // State for time period filter
  const [sinceHours, setSinceHours] = useState<number>(24);
  
  // State for drivers and slots
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Local state
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignmentType, setAssignmentType] = useState<'driver-to-slot' | 'slot-to-driver'>('driver-to-slot');
  const [tabValue, setTabValue] = useState(0);
  const [driverFilter, setDriverFilter] = useState('');
  const [slotFilter, setSlotFilter] = useState<SlotType | 'all'>('dock');
  const [driverSortBy, setDriverSortBy] = useState<'default' | 'mdt' | 'checkin' | 'appointment'>('default');
  const [autoAssignStatus, setAutoAssignStatus] = useState<{
    active: boolean;
    current: number;
    total: number;
    success: number;
    error: number;
  }>({
    active: false,
    current: 0,
    total: 0,
    success: 0,
    error: 0
  });
  const [showAutoAssignResult, setShowAutoAssignResult] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdTask, setCreatedTask] = useState<any>(null);
  const [lockedSlots, setLockedSlots] = useState<Set<string>>(new Set());
  const [taskBasedLockedSlots, setTaskBasedLockedSlots] = useState<Set<string>>(new Set());
  const [activeLocks, setActiveLocks] = useState<any[]>([]);
  
  // Task details modal state
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState<boolean>(false);
  const [selectedTaskForDetails, setSelectedTaskForDetails] = useState<any>(null);
  
  // Assignment loading state
  const [isAssigning, setIsAssigning] = useState<boolean>(false);
  
  // Assignment notes state
  const [assignmentNotes, setAssignmentNotes] = useState<string>('');
  
  // Auto-complete state (default to true)
  const [autoComplete, setAutoComplete] = useState<boolean>(true);

  // Add drag and drop state
  const [draggedDriver, setDraggedDriver] = useState<Driver | null>(null);
  const [dragOverSpot, setDragOverSpot] = useState<string | null>(null);
  const [dragOverZone, setDragOverZone] = useState<string | null>(null);
  
  // Manual refresh loading state
  const [isRefreshingData, setIsRefreshingData] = useState(false);
  
  // Toggle for showing unavailable spots
  const [showUnavailable, setShowUnavailable] = useState(false);
  
  // Add a useRef for the refresh interval
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Router location for deep linking
  const location = useLocation();

  // Handle deep linking to specific driver assignment
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const driverId = searchParams.get('driver');
    
    console.log('DriverSlotAssignment: Deep linking check:', { 
      driverId, 
      driversLength: drivers.length, 
      locationSearch: location.search,
      isLoading 
    });
    
    if (driverId && drivers.length > 0 && !isLoading) {
      console.log('DriverSlotAssignment: Looking for driver ID:', driverId);
      console.log('DriverSlotAssignment: Available assignments:', drivers);
      console.log('DriverSlotAssignment: Driver IDs in list:', drivers.map(d => d.id));
      
      // Find the driver assignment with the specified ID
      const targetDriver = drivers.find(driver => driver.id === driverId);
      console.log('DriverSlotAssignment: Found target driver assignment:', targetDriver);
      
      if (targetDriver) {
        console.log('DriverSlotAssignment: Opening modal for driver assignment:', targetDriver.name);
        // Auto-open the assignment modal with this driver pre-selected
        setSelectedDriver(targetDriver);
        setAssignmentType('driver-to-slot');
        setShowAssignModal(true);
      } else {
        console.log('DriverSlotAssignment: Driver assignment not found in drivers list');
      }
    }
  }, [location.search, drivers, isLoading]);
  
  // Helper function to generate consistent colors for zones
  const generateZoneColor = (zoneName: string): string => {
    const colors = [
      '#3B82F6', // blue-500
      '#10B981', // emerald-500
      '#F59E0B', // amber-500
      '#EF4444', // red-500
      '#8B5CF6', // violet-500
      '#06B6D4', // cyan-500
      '#84CC16', // lime-500
      '#F97316', // orange-500
      '#EC4899', // pink-500
      '#6366F1', // indigo-500
    ];
    
    // Generate a consistent color based on zone name
    let hash = 0;
    for (let i = 0; i < zoneName.length; i++) {
      hash = ((hash << 5) - hash + zoneName.charCodeAt(i)) & 0xffffffff;
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Fetch facilities using GraphQL
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        setIsLoading(true);
        
        // GraphQL query for facilities
        const facilitiesQuery = `
          query {
            facilities {
              id
              name
            }
          }
        `;

        // Get auth token from cookie
        const authToken = getAuthToken();

        // Fetch facilities using GraphQL
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: facilitiesQuery
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        setFacilities(result.data.facilities);
        
        // Validate localStorage facility still exists, otherwise set default
        const storedFacility = loadFacilityFromStorage();
        if (storedFacility) {
          const facilityExists = result.data.facilities.find((f: any) => f.id === storedFacility.id);
          if (!facilityExists) {
            // Stored facility no longer exists, set default
            if (result.data.facilities.length > 0) {
              setSelectedFacility(result.data.facilities[0]);
            }
          }
          // If facility exists, keep the localStorage value (already set in useState initializer)
        } else {
          // No stored facility, set default
          if (result.data.facilities.length > 0) {
            setSelectedFacility(result.data.facilities[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch facilities:', err);
        setError('Failed to fetch facilities');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFacilities();
  }, []);

  // Define fetchFacilityLocks as a standalone function so it can be reused
  const fetchFacilityLocks = async () => {
    if (selectedFacility) {
      try {
        // Get auth token from cookie or localStorage
        const authToken = getAuthToken();

        // GraphQL query for facility locks
        const facilityLocksQuery = `
          query GetFacilityLocks {
            facilityLocks(facilityId: "${selectedFacility.id}") {
              reason
              resource_uuid
              model_name
              operation_type
              created_by
              task_uuid
              created_at
              expires_at
              namespace
              facility_id
              resource {
                ... on YardSpotObject {
                  uuid
                  name
                }
                ... on DockObject {
                  uuid
                  name
                }
                ... on Trailer {
                  uuid
                  trailerNumber
                }
              }
            }
          }
        `;

        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: facilityLocksQuery
          })
        });

        const result = await response.json();

        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        if (result.data && result.data.facilityLocks) {
          const locks = result.data.facilityLocks;
          const lockedSlotIds = new Set<string>();
          const allActiveLocks: any[] = [];

          locks.forEach((lock: any) => {
            // Check if this lock applies to a yard spot or dock
            const isYardSpotLock = lock.model_name === 'YardSpot' ||
                                  lock.model_name === 'YardSpotObject' ||
                                  (lock.resource && lock.resource.__typename === 'YardSpotObject');

            const isDockLock = lock.model_name === 'DockObject' ||
                              lock.model_name === 'Dock' ||
                              (lock.resource && lock.resource.__typename === 'DockObject');

            if (isYardSpotLock || isDockLock) {
              // Store the lock information for linking to tasks
              allActiveLocks.push(lock);

              // Add the resource UUID to locked slots
              const resourceUuid = lock.resource_uuid || lock.resource?.uuid;
              if (resourceUuid) {
                lockedSlotIds.add(resourceUuid);
              }
            }
          });

          setTaskBasedLockedSlots(lockedSlotIds);
          setActiveLocks(allActiveLocks);
        }
      } catch (error) {
        console.error('Failed to fetch facility locks:', error);
      }
    }
  };

  // Fetch drivers and yard spots using GraphQL
  const fetchDriversAndSlots = async (showIndicator = false) => {
    if (!selectedFacility) return;
    
    if (showIndicator) {
      setIsRefreshingData(true);
    }
    
    try {
      // Get auth token from cookie
      const authToken = getAuthToken();

      // Combined GraphQL query for both unassigned drivers, yard spots, and dock doors
      const combinedQuery = `
        query GetUnassignedDriversAndYardAndDocks {
          unassignedLiveDriverAssignments(facilityId: "${selectedFacility.id}", sinceHours: ${sinceHours}) {
            success
            message
            totalCount
            driverAssignments {
              id
              shipmentId
              userId
              driverId
              phone
              assignedAt
              revokedAt
              checkIn
              checkOut
              status
              archived
              archivedAt
              createdAt
              updatedAt
              
              # Now you can also fetch related driver data
              driver {
                id
                firstName
                lastName
              }
              
              # And related shipment data
              shipment {
                id
                referenceNumber
                direction
                metadata
                trailer {
                  uuid
                  trailer_number
                  trailer_type
                  refrigerated
                  length_feet
                  weight_capacity
                  volume_capacity
                  
                  
                  # Carrier info (if available)
                  carrier {
                    id
                    name
                  }
                }
              }
            }
          }
          
          yard(facility_id: "${selectedFacility?.id || "1"}") {
            facility_id
            facility_zones {
              facility_zone_name
              facility_zone_uuid
              yard_spots {
                yard_spot_uuid
                yard_spot_name
                yard_spot_facility_zone_uuid
                yard_spot_status
                order_number
                trailer_lineage_uuid
                trailer_state_uuid
                trailer_name
                trailer_uuid
                carrier_name
                shipment_id
                shipment_reference_number
                driver_id
                driver_name
              }
            }
          }
          
          warehouse(facility_id: "${selectedFacility?.id || "1"}") {
            facility_id
            warehouse_zones {
              zone_uuid
              zone_name
              docks {
                dock_uuid
                dock_name
                order_number
                trailer_uuid
                configuration
              }
            }
          }
        }
      `;

      // Execute the combined query
      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: combinedQuery
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Transform drivers data
      const transformedDrivers: Driver[] = result.data.unassignedLiveDriverAssignments.driverAssignments.map((assignment: any) => {
        // Parse metadata if it's a JSON string
        const metadata = typeof assignment.shipment?.metadata === 'string' 
          ? JSON.parse(assignment.shipment.metadata) 
          : assignment.shipment?.metadata;
        
        return {
          id: assignment.id || assignment.shipmentId, // Use assignment ID to ensure uniqueness
          driverId: assignment.driverId || assignment.driver?.id, // Store actual driver ID
          name: assignment.driver 
            ? `${assignment.driver.firstName} ${assignment.driver.lastName}`.trim()
            : 'Unknown Driver',
          loadNumber: assignment.shipment?.referenceNumber || assignment.shipmentId,
          direction: formatDirection(assignment.shipment?.direction || ''), // Use direction from shipment data
          type: formatShipmentType(metadata?.shipment_type),
          truckNumber: '', // Not available in current schema
          trailerNumber: assignment.shipment?.trailer?.trailer_number || '', // Get trailer number from shipment
          arrivalTime: new Date(assignment.createdAt).toLocaleString(), // Display formatted time
          checkInTime: assignment.createdAt, // Raw timestamp for sorting
          shipment_id: assignment.shipmentId,
          driver: {
            ...assignment.driver,
            shipment: assignment.shipment
          },
          shipment: assignment.shipment ? {
            ...assignment.shipment,
            metadata: metadata
          } : undefined
        };
      });

      // Transform yard spots and dock doors data - only available spots and organize by zones
      const transformedSlots: Slot[] = [];
      const zoneMap = new Map<string, { name: string; spots: Slot[] }>();
      
      // Process yard spots
      if (result.data?.yard?.facility_zones) {
        // Sort zones alphabetically by name
        const sortedYardZones = [...result.data.yard.facility_zones].sort((a, b) => 
          a.facility_zone_name.localeCompare(b.facility_zone_name)
        );
        
        sortedYardZones.forEach((zone: any) => {
          if (zone.yard_spots) {
            const allSpots: Slot[] = [];
            
            // Sort yard spots by order_number
            const sortedYardSpots = [...zone.yard_spots].sort((a, b) => 
              (a.order_number || 0) - (b.order_number || 0)
            );
            
            sortedYardSpots.forEach((spot: any) => {
              // Include all yard spots, but set status based on trailer assignment
              const slot: Slot = {
                id: spot.yard_spot_uuid,
                number: spot.yard_spot_name,
                type: 'yard',
                status: spot.trailer_uuid ? 'in-use' : 'available',
                zoneId: zone.facility_zone_uuid,
                zoneName: zone.facility_zone_name
              };
              
              transformedSlots.push(slot);
              allSpots.push(slot);
            });
            
            // Add zones that have any spots (not just available ones)
            if (allSpots.length > 0) {
              zoneMap.set(zone.facility_zone_uuid, {
                name: zone.facility_zone_name,
                spots: allSpots
              });
            }
          }
        });
      }
      
      // Process dock doors
      if (result.data?.warehouse?.warehouse_zones) {
        // Sort warehouse zones alphabetically by name
        const sortedWarehouseZones = [...result.data.warehouse.warehouse_zones].sort((a, b) => 
          a.zone_name.localeCompare(b.zone_name)
        );
        
        sortedWarehouseZones.forEach((zone: any) => {
          if (zone.docks) {
            const allDocks: Slot[] = [];
            
            // Sort docks by order_number
            const sortedDocks = [...zone.docks].sort((a, b) => 
              (a.order_number || 0) - (b.order_number || 0)
            );
            
            sortedDocks.forEach((dock: any) => {
              // Include all docks, but set status based on trailer assignment and configuration
              let status: SlotStatus = 'available';

              // Parse configuration if it's a JSON string
              const config = typeof dock.configuration === 'string'
                ? (() => { try { return JSON.parse(dock.configuration); } catch { return null; } })()
                : dock.configuration;

              // Check configuration status and paused state
              if (config?.status === 'unavailable' || config?.paused) {
                status = 'unavailable';
              } else if (dock.trailer_uuid) {
                status = 'in-use';
              }
              
              const slot: Slot = {
                id: dock.dock_uuid,
                number: dock.dock_name,
                type: 'dock',
                status: status,
                zoneId: zone.zone_uuid,
                zoneName: zone.zone_name
              };
              
              transformedSlots.push(slot);
              allDocks.push(slot);
            });
            
            // Add or merge dock zone with existing yard zone (now includes all docks)
            if (allDocks.length > 0) {
              const existingZone = zoneMap.get(zone.zone_uuid);
              if (existingZone) {
                // Zone already exists, add docks to it
                existingZone.spots.push(...allDocks);
              } else {
                // New zone for docks
                zoneMap.set(zone.zone_uuid, {
                  name: zone.zone_name,
                  spots: allDocks
                });
              }
            }
          }
        });
      }

      // Create zones array with colors
      const transformedZones: Zone[] = Array.from(zoneMap.entries()).map(([zoneId, zoneData]) => ({
        id: zoneId,
        name: zoneData.name,
        color: generateZoneColor(zoneData.name),
        spots: zoneData.spots // Keep the order_number sorting we already applied
      }));

      setDrivers(transformedDrivers);
      setSlots(transformedSlots);
      setZones(transformedZones);
      setError(null);

      // Refresh facility locks as part of the data refresh
      await fetchFacilityLocks();

    } catch (err) {
      console.error('Failed to fetch drivers and slots:', err);
      setError('Failed to fetch drivers and slots');
    } finally {
      if (showIndicator) {
        setTimeout(() => setIsRefreshingData(false), 300);
      }
    }
  };

  // Fetch data when facility or time period changes
  useEffect(() => {
    if (selectedFacility) {
      fetchDriversAndSlots(true);
    }
  }, [selectedFacility, sinceHours]);

  // Fetch facility locks when facility changes
  useEffect(() => {
    fetchFacilityLocks();
  }, [selectedFacility]);

  // Define the refresh function with useCallback to avoid dependency issues
  const memoizedRefreshData = React.useCallback(() => fetchDriversAndSlots(false), [selectedFacility, sinceHours]);

  // Update the auto-refresh interval
  useEffect(() => {
    if (!selectedFacility) return;
    
    // Clear existing interval if any
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    
    // Set up new interval - refresh silently (no indicator)
    refreshIntervalRef.current = setInterval(() => {
      memoizedRefreshData();
    }, 30000); // 30 seconds
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [selectedFacility, memoizedRefreshData]);

  // Manual refresh function - shows indicator
  const handleManualRefresh = () => {
    if (!isRefreshingData && selectedFacility) {
      fetchDriversAndSlots(true);
    }
  };

  // Function to cancel a task
  const handleCancelTask = async (taskId: string) => {
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const cancelTaskMutation = `
        mutation CancelTask($taskUuid: String!) {
          cancel_task(uuid: $taskUuid) {
            uuid
            task_status {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: cancelTaskMutation,
          variables: { taskUuid: taskId }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh data to update the UI
      fetchDriversAndSlots(true);
    } catch (error) {
      console.error('Failed to cancel task:', error);
      throw error;
    }
  };

  // Function to complete a task
  const handleCompleteTask = async (taskId: string) => {
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const completeTaskMutation = `
        mutation CompleteTask($taskUuid: String!) {
          complete_task(uuid: $taskUuid) {
            uuid
            task_status {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: completeTaskMutation,
          variables: { taskUuid: taskId }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh data to update the UI
      fetchDriversAndSlots(true);
    } catch (error) {
      console.error('Failed to complete task:', error);
      throw error;
    }
  };

  // Function to complete a movement task
  const handleCompleteMovementTask = async (taskId: string) => {
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const completeMovementTaskMutation = `
        mutation CompleteMovementTask($input: CompleteMovementTaskInput!) {
          completeMovementTask(input: $input) {
            uuid
            taskStatus {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: completeMovementTaskMutation,
          variables: { 
            input: { 
              taskUuid: taskId 
            } 
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh data to update the UI
      fetchDriversAndSlots(true);
    } catch (error) {
      console.error('Failed to complete movement task:', error);
      throw error;
    }
  };

  // Function to fetch and show task details from a lock
  const handleShowTaskFromLock = async (lock: any) => {
    if (!lock.task_uuid) {
      console.error('No task UUID in lock object');
      return;
    }

    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // GraphQL query to fetch task by UUID
      const taskQuery = `
        query GetTask($taskUuid: String!) {
          task(uuid: $taskUuid) {
            uuid
            data
            task_type {
              uuid
              type
            }
            task_status {
              uuid
              status
            }
            from_location
            to_location
            trailer {
              uuid
              trailer_number
            }
            driver {
              id
              firstName
              lastName
            }
            created_at
            updated_at
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: taskQuery,
          variables: { taskUuid: lock.task_uuid }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        console.error('Failed to fetch task:', result.errors);
        // If task fetch fails, create a fallback task object from the lock
        const fallbackTask = {
          uuid: lock.task_uuid,
          task_status: { status: 'ACTIVE' },
          task_type: { type: lock.operation_type || 'Movement Task' },
          from_location: `${lock.model_name}: ${lock.resource?.name || lock.resource_uuid}`,
          to_location: 'Unknown',
          trailer: lock.resource?.trailerNumber ? { trailer_number: lock.resource.trailerNumber } : null,
          created_at: lock.created_at,
          data: {
            reason: lock.reason,
            operationType: lock.operation_type
          }
        };
        setSelectedTaskForDetails(fallbackTask);
        setShowTaskDetailsModal(true);
        return;
      }

      if (result.data?.task) {
        setSelectedTaskForDetails(result.data.task);
        setShowTaskDetailsModal(true);
      } else {
        console.error('No task found with UUID:', lock.task_uuid);
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
      // Show fallback task details from lock
      const fallbackTask = {
        uuid: lock.task_uuid,
        task_status: { status: 'ACTIVE' },
        task_type: { type: lock.operation_type || 'Movement Task' },
        from_location: `${lock.model_name}: ${lock.resource?.name || lock.resource_uuid}`,
        to_location: 'Unknown',
        trailer: lock.resource?.trailerNumber ? { trailer_number: lock.resource.trailerNumber } : null,
        created_at: lock.created_at,
        data: {
          reason: lock.reason,
          operationType: lock.operation_type
        }
      };
      setSelectedTaskForDetails(fallbackTask);
      setShowTaskDetailsModal(true);
    }
  };

  // Helper function to find locks affecting a specific slot
  const getLocksForSlot = (slotId: string) => {
    return activeLocks.filter((lock: any) => {
      const resourceUuid = lock.resource_uuid || lock.resource?.uuid;
      return resourceUuid === slotId;
    });
  };

  // Create movement task for trailer assignment
  const handleTrailerMovement = async (driver: Driver, slot: Slot, notes?: string): Promise<boolean> => {
    try {
      // Get auth token from cookie
      const authToken = getAuthToken();

      // Determine movement type based on slot type
      const movementType = slot.type === 'dock' ? 'external_to_warehouse' : 'external_to_yard';
      
      // Create movement task mutation
      const createMovementTaskMutation = `
        mutation CreateMovementTask(
          $trailer_uuid: String!,
          $movement_type: String!,
          $facility_id: ID!,
          $created_by_facility_id: ID,
          $warehouse_zone_uuid: String,
          $dock_uuid: String,
          $facility_zone_uuid: String,
          $yard_spot_uuid: String,
          $driver_id: String!,
          $shipment_id: String!,
          $force: Boolean,
          $auto_complete: Boolean
        ) {
          create_movement_task(input: {
            trailer_uuid: $trailer_uuid
            movement_type: $movement_type
            facility_id: $facility_id
            created_by_facility_id: $created_by_facility_id
            warehouse_zone_uuid: $warehouse_zone_uuid
            dock_uuid: $dock_uuid
            facility_zone_uuid: $facility_zone_uuid
            yard_spot_uuid: $yard_spot_uuid
            driver_id: $driver_id
            shipment_id: $shipment_id
            force: $force
            auto_complete: $auto_complete
          }) {
            uuid
            data
            task_type {
              uuid
              type
            }
            task_status {
              uuid
              status
            }
            from_location
            to_location
            trailer {
              uuid
              trailer_number
            }
          }
        }
      `;

      // Prepare variables for the mutation
      // Note: We're mapping available driver data to the movement task structure
      const variables: any = {
        trailer_uuid: driver.shipment?.trailer?.uuid || driver.bol_id, // Use actual trailer UUID from shipment data
        movement_type: movementType,
        facility_id: selectedFacility?.id.toString() || "1",
        // The page shows one facility: the move is created for it.
        created_by_facility_id: selectedFacility?.id.toString() || "1",
        driver_id: driver.driverId, // Use actual driver ID, not assignment ID
        shipment_id: driver.shipment_id,
        force: false
      };

      // Add slot-specific variables based on slot type
      if (slot.type === 'dock') {
        variables.warehouse_zone_uuid = slot.zoneId;
        variables.dock_uuid = slot.id;
      } else {
        variables.facility_zone_uuid = slot.zoneId;
        variables.yard_spot_uuid = slot.id;
      }

      // Add auto_complete flag if enabled
      if (autoComplete) {
        variables.auto_complete = true;
      }

      console.log('Creating movement task with variables:', variables);

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: createMovementTaskMutation,
          variables
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        return false;
      }

      // Check if the movement task was created successfully
      if (result.data?.create_movement_task?.uuid) {
        console.log('Movement task created successfully:', result.data.create_movement_task);
        
        // Lock the slot to show it's in a pending state
        setLockedSlots(prev => new Set(prev).add(slot.id));
        
        // Store the task data for success modal
        setCreatedTask(result.data.create_movement_task);
        setShowSuccessModal(true);
        
        // If notes were provided, update the trailer with the notes
        if (notes && notes.trim()) {
          try {
            const updateTrailerMutation = `
              mutation UpdateTrailer($uuid: String!, $input: TrailerUpdateInput!) {
                updateTrailer(uuid: $uuid, input: $input) {
                  uuid
                  trailer_number
                  active_lineage {
                    uuid
                    notes
                  }
                }
              }
            `;

            const updateResponse = await fetch(GRAPHQL_API_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify({
                query: updateTrailerMutation,
                variables: {
                  uuid: variables.trailer_uuid,
                  input: {
                    notes: notes.trim()
                  }
                }
              })
            });

            const updateResult = await updateResponse.json();
            
            if (updateResult.errors) {
              console.error('Failed to update trailer notes:', updateResult.errors);
              // Don't fail the whole operation, just log the error
            } else {
              console.log('Trailer notes updated successfully');
            }
          } catch (noteError) {
            console.error('Error updating trailer notes:', noteError);
            // Don't fail the whole operation, just log the error
          }
        }
        
        return true;
      } else {
        console.error('Failed to create movement task:', result.data);
        return false;
      }
    } catch (error) {
      console.error('Error creating movement task:', error);
      return false;
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, driver: Driver) => {
    // Disable drag and drop
    e.preventDefault();
    return false;
    
    // Create a custom drag image that's smaller
    const dragElement = e.currentTarget.cloneNode(true) as HTMLElement;
    dragElement.style.transform = 'scale(0.5)';
    dragElement.style.opacity = '0.8';
    dragElement.style.position = 'absolute';
    dragElement.style.top = '-1000px';
    dragElement.style.left = '-1000px';
    dragElement.style.pointerEvents = 'none';
    dragElement.style.background = '#dbeafe';
    dragElement.style.border = '2px solid #3b82f6';
    dragElement.style.borderRadius = '8px';
    
    document.body.appendChild(dragElement);
    
    // Set the custom drag image
    e.dataTransfer.setDragImage(dragElement, 
      dragElement.offsetWidth / 4,  // Center point for smaller element
      dragElement.offsetHeight / 4
    );
    
    // Clean up the temporary element after a short delay
    setTimeout(() => {
      if (document.body.contains(dragElement)) {
        document.body.removeChild(dragElement);
      }
    }, 0);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedDriver(null);
    setDragOverZone(null);
    setDragOverSpot(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnterSpot = (e: React.DragEvent, spot: Slot) => {
    e.preventDefault();
    setDragOverSpot(spot.id);
    setDragOverZone(spot.zoneId || null);
  };

  const handleDragLeaveSpot = (e: React.DragEvent) => {
    // Only clear if we're not entering a child element
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverSpot(null);
      setDragOverZone(null);
    }
  };

  const handleDropOnSpot = async (e: React.DragEvent, slot: Slot) => {
    e.preventDefault();
    setDragOverSpot(null);
    setDragOverZone(null);

    if (!draggedDriver) return;

    console.log(`Dropped driver ${draggedDriver.name} onto spot ${slot.number} in zone ${slot.zoneName}`);

    // Create movement task for this assignment
    const success = await handleTrailerMovement(draggedDriver, slot);
    
    if (success) {
      // Update slot in local state
      const updatedSlots = slots.map(s => 
        s.id === slot.id 
          ? { ...s, status: 'in-use' as SlotStatus, assignedDriverId: draggedDriver.id } 
          : s
      );

      setSlots(updatedSlots);
      
      // Refresh data to get updated state
      fetchDriversAndSlots(true);
    } else {
      alert(`Failed to create movement task for ${draggedDriver.name} to ${slot.type} ${slot.number}. Please try again.`);
    }

    setDraggedDriver(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const openAssignModal = (type: 'driver-to-slot' | 'slot-to-driver', driver?: Driver, slot?: Slot) => {
    setAssignmentType(type);
    if (driver) setSelectedDriver(driver);
    if (slot) setSelectedSlot(slot);
    setShowAssignModal(true);
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedDriver(null);
    setSelectedSlot(null);
    setIsAssigning(false);
    setAssignmentNotes(''); // Clear notes when closing modal
    setAutoComplete(true); // Reset to default (checked)
  };

  const handleSingleAssignment = async (driver: Driver, slot: Slot, notes?: string): Promise<boolean> => {
    // Use the movement task creation instead of the old assignment logic
    return await handleTrailerMovement(driver, slot, notes);
  };

  const handleAssignment = async () => {
    if (!selectedDriver || !selectedSlot) return;

    setIsAssigning(true);
    
    try {
      const success = await handleSingleAssignment(selectedDriver, selectedSlot, assignmentNotes);
      
      if (success) {
        // Update slot in local state
        const updatedSlots = slots.map(slot => 
          slot.id === selectedSlot.id 
            ? { ...slot, status: 'in-use' as SlotStatus, assignedDriverId: selectedDriver.id } 
            : slot
        );

        setSlots(updatedSlots);
        setAssignmentNotes(''); // Clear notes after successful assignment
        closeAssignModal();
        
        // Refresh data without causing a full re-render
        fetchDriversAndSlots(true);
      } else {
        alert(`Failed to create movement task for ${selectedDriver.name} to ${selectedSlot.type} ${selectedSlot.number}. Please try again.`);
      }
    } catch (error) {
      console.error('Assignment error:', error);
      alert('An error occurred while creating the assignment. Please try again.');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleAutoAssignAll = async () => {
    // Get all unassigned drivers
    const driversToAssign = [...drivers];
    
    // Get all available slots
    const slotsToFill = [...slots];
    
    // No drivers or slots to assign
    if (driversToAssign.length === 0 || slotsToFill.length === 0) {
      return;
    }
    
    // Determine how many assignments we'll make
    const maxAssignments = Math.min(driversToAssign.length, slotsToFill.length);
    
    // Initialize auto-assign status
    setAutoAssignStatus({
      active: true,
      current: 0,
      total: maxAssignments,
      success: 0,
      error: 0
    });
    
    // Track updates to slots
    let updatedSlots = [...slots];
    
    // Process each assignment individually with delay to show the process
    for (let i = 0; i < maxAssignments; i++) {
      const driver = driversToAssign[i];
      const slot = slotsToFill[i];
      
      // Update status to show current assignment
      setAutoAssignStatus(prev => ({
        ...prev,
        current: i + 1
      }));
      
      // Make the API call for this assignment
      const success = await handleSingleAssignment(driver, slot);
      
      // Update success/error count
      setAutoAssignStatus(prev => ({
        ...prev,
        success: success ? prev.success + 1 : prev.success,
        error: success ? prev.error : prev.error + 1
      }));
      
      if (success) {
        // Find the slot in the updatedSlots array and update it
        updatedSlots = updatedSlots.map(s => 
          s.id === slot.id 
            ? { ...s, status: 'in-use' as SlotStatus, assignedDriverId: driver.id } 
            : s
        );
        
        // Update slots after each successful assignment to show real-time progress
        setSlots(updatedSlots);
        
        // Add a small delay to make the visual process more visible
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    // Show results
    setShowAutoAssignResult(true);
    
    // Reset status after a delay
    setTimeout(() => {
      setAutoAssignStatus({
        active: false,
        current: 0,
        total: 0,
        success: 0,
        error: 0
      });
    }, 3000);
  };

  const handleUnassignment = (driverId: string, slotId: string) => {
    // Update slot
    const updatedSlots = slots.map(slot => 
      slot.id === slotId 
        ? { ...slot, status: 'available' as SlotStatus, assignedDriverId: null } 
        : slot
    );

    setSlots(updatedSlots);
  };

  const handleClearAll = () => {    
    const updatedSlots = slots.map(slot => ({
      ...slot,
      status: slot.status === 'in-use' ? 'available' as SlotStatus : slot.status,
      assignedDriverId: null
    }));
    
    // Update state
    setSlots(updatedSlots);
  };

  const getStatusColor = (status: SlotStatus): string => {
    switch (status) {
      case 'available':
        return 'bg-green-500';
      case 'in-use':
        return 'bg-red-500';
      case 'unavailable':
        return 'bg-gray-500';
      case 'scheduled':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getDriverTypeColor = (type: string): string => {
    switch (type) {
      case 'outbound':
        return 'bg-blue-100 text-blue-800';
      case 'inbound':
        return 'bg-green-100 text-green-800';
      case 'both':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get facility options for filter
  const facilityOptions = formatFacilityOptions(facilities);

  // Time period options
  const timeOptions = [
    { value: 24, label: '24 hours' },
    { value: 48, label: '48 hours' },
    { value: 2400000, label: 'MAX' }
  ];

  // Driver sort options
  const driverSortOptions = [
    { value: 'default', label: 'Default' },
    { value: 'mdt', label: 'Must Depart Time (MDT)' },
    { value: 'checkin', label: 'Check-in Time' },
    { value: 'appointment', label: 'Appointment Time' }
  ];

  // Filter and sort unassigned drivers  
  const filteredDrivers = drivers.filter(driver => !slots.some(slot => slot.assignedDriverId === driver.id));
  const searchFiltered = filteredDrivers.filter(driver => 
    !driverFilter || 
    driver.name.toLowerCase().includes(driverFilter.toLowerCase()) || 
    driver.loadNumber?.toLowerCase().includes(driverFilter.toLowerCase())
  );
  
  const unassignedDrivers = [...searchFiltered].sort((a, b) => {
      if (driverSortBy === 'default') {
        return 0;
      }
      
      if (driverSortBy === 'mdt') {
        const aMdt = a.shipment?.metadata?.appointment_must_depart_by_date;
        const bMdt = b.shipment?.metadata?.appointment_must_depart_by_date;
        
        const hasA = aMdt && aMdt !== '';
        const hasB = bMdt && bMdt !== '';
        
        if (!hasA && !hasB) return 0;
        if (!hasA) return 1;
        if (!hasB) return -1;
        
        return new Date(aMdt).getTime() - new Date(bMdt).getTime();
      }
      
      if (driverSortBy === 'checkin') {
        // Sort oldest first (ascending)
        return new Date(a.checkInTime || 0).getTime() - new Date(b.checkInTime || 0).getTime();
      }
      
      if (driverSortBy === 'appointment') {
        const aAppt = a.shipment?.metadata?.appointment_date;
        const bAppt = b.shipment?.metadata?.appointment_date;
        
        const hasA = aAppt && aAppt !== '';
        const hasB = bAppt && bAppt !== '';
        
        if (!hasA && !hasB) return 0;
        if (!hasA) return 1;
        if (!hasB) return -1;
        
        return new Date(aAppt).getTime() - new Date(bAppt).getTime();
      }
      
      return 0;
    });

  // Combine all locked slots (task-based and manual locks)
  const allLockedSlots = mergeLockSets(taskBasedLockedSlots, lockedSlots);
  
  // Filter slots based on type and availability toggle
  const filteredSlots = slots
    .filter(spot => slotFilter === 'all' ? true : spot.type === slotFilter)
    .filter(slot => {
      if (showUnavailable) {
        return true; // Show all slots when "show unavailable" is enabled
      } else {
        // Only show available slots that are not locked
        return slot.status === 'available' && !allLockedSlots.has(slot.id);
      }
    });
  
  // Separate available slots for counts and assignment logic
  // When showUnavailable is false, filteredSlots already excludes locked spots
  const availableSlots = showUnavailable 
    ? filteredSlots.filter(slot => slot.status === 'available' && !allLockedSlots.has(slot.id))
    : filteredSlots; // Already filtered to exclude locked spots

  // Filter assigned pairs
  const assignedPairs = slots
    .filter(slot => slot.assignedDriverId)
    .map(slot => {
      const driver = drivers.find(d => d.id === slot.assignedDriverId);
      return { slot, driver };
    })
    .filter(pair => pair.driver !== undefined);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center mt-4 w-full">
        <CircularProgress />
        <p className="mt-2">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 text-red-500">
        <h1 className="font-bold text-xl mb-3">Error</h1>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="p-5 flex flex-col">
      <div className="shadow bg-[#FBFBFB] p-3 mb-5">
        <div className="flex flex-col md:flex-row gap-3 mb-3">
          <div className="flex gap-3 ml-auto">
            <div className="flex items-center mr-2">
              {isRefreshingData ? (
                <div className="flex items-center">
                  <CircularProgress size={16} className="mr-2" />
                  <Typography variant="caption" color="textSecondary">
                    Refreshing...
                  </Typography>
                </div>
              ) : (
                <IconButton 
                  size="small" 
                  onClick={handleManualRefresh} 
                  title="Refresh"
                >
                  <Refresh fontSize="small" />
                </IconButton>
              )}
            </div>
            <Select
              options={facilityOptions}
              isSearchable={true}
              isClearable
              value={selectedFacility ? {
                value: selectedFacility.id,
                label: selectedFacility.name,
                facility: selectedFacility
              } : null}
              placeholder="Facilities"
              onChange={(selectedOption: any) => {
                setSelectedFacility(selectedOption?.facility || null);
              }}
              className="w-60"
            />
            <Select
              options={timeOptions}
              value={timeOptions.find(option => option.value === sinceHours)}
              placeholder="Time Period"
              onChange={(selectedOption: any) => {
                setSinceHours(selectedOption?.value || 48);
              }}
              className="w-32"
              isSearchable={false}
            />
          </div>
        </div>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Driver Assignment" />
          <Tab label="Prechecked Drivers" />
        </Tabs>
      </Box>

      {/* Zone Summary - Hidden for now */}
      {/* {zones.length > 0 && (
        <div className="bg-white p-4 shadow mb-5">
          <h3 className="font-semibold text-lg mb-3">Yard Zones Overview</h3>
          <div className="flex flex-wrap gap-3">
            {zones.map(zone => (
              <div 
                key={zone.id}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg border-2"
                style={{ borderColor: zone.color }}
              >
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: zone.color }}
                ></div>
                <span className="font-medium text-sm">{zone.name}</span>
                <span 
                  className="text-xs px-2 py-1 rounded-full text-white font-medium"
                  style={{ backgroundColor: zone.color }}
                >
                  {zone.spots.length} available
                </span>
              </div>
            ))}
          </div>
        </div>
      )} */}


      {tabValue === 0 && (
        <div className="flex flex-col md:flex-row gap-5 flex-1">
          {/* Unassigned Drivers Panel */}
          <div className="w-full md:w-1/3 bg-white p-4 shadow flex flex-col">
            <div className="flex flex-col gap-2 mb-4">
              <div className="flex justify-between items-center">
                <h2 className="font-bold text-lg">Unassigned Drivers ({unassignedDrivers.length})</h2>
              </div>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  placeholder="Search drivers..." 
                  className="border rounded p-2 text-sm flex-1"
                  value={driverFilter}
                  onChange={(e) => setDriverFilter(e.target.value)}
                />
                <Select
                  options={driverSortOptions}
                  value={driverSortOptions.find(option => option.value === driverSortBy)}
                  placeholder="Sort by"
                  onChange={(selectedOption: any) => {
                    setDriverSortBy(selectedOption?.value || 'default');
                  }}
                  className="w-48"
                  isSearchable={false}
                />
              </div>
            </div>
            
            <div className="max-h-[576px] overflow-y-auto">
              {unassignedDrivers.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  No unassigned drivers
                </div>
              ) : (
                <div className="space-y-2">
                  {unassignedDrivers.map(driver => (
                    <div 
                      key={driver.id} 
                      className={`p-3 border rounded mb-3 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-all duration-200 ${
                        draggedDriver?.id === driver.id ? 'opacity-60 scale-50 rotate-2 shadow-xl border-blue-400' : ''
                      }`}
                      onClick={() => openAssignModal('driver-to-slot', driver)}
                      draggable={false}
                      onDragStart={(e) => handleDragStart(e, driver)}
                      onDragEnd={handleDragEnd}
                      style={{
                        cursor: draggedDriver?.id === driver.id ? 'grabbing' : 'grab',
                        transform: draggedDriver?.id === driver.id ? 'scale(0.5) rotate(2deg)' : 'none'
                      }}
                    >
                      <div className="flex justify-between">
                        <span className="font-bold">{driver.name}</span>
                        <span className={`text-xs px-2 py-1 rounded ${getDriverTypeColor(driver.type)}`}>
                          {driver.type.charAt(0).toUpperCase() + driver.type.slice(1)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">Ref #: {driver.loadNumber}</p>
                      {driver.shipment?.trailer && (
                        <div className="text-sm">
                          <p>
                            <span className="font-medium">Trailer:</span> {driver.shipment.trailer.trailer_number || 'N/A'}
                            {driver.shipment.trailer.trailer_type && (
                              <span className="text-gray-500 ml-1">({driver.shipment.trailer.trailer_type})</span>
                            )}
                          </p>

                          {driver.direction && (
                            <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 mr-2">
                              {driver.direction}
                            </span>
                          )}

                          {driver.type && (
                            <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 mr-2">
                              {driver.type}
                            </span>
                          )}

                          {driver.shipment.trailer.refrigerated && (
                            <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 mr-2">
                              Refrigerated
                            </span>
                          )}
                          {driver.shipment.trailer.length_feet && driver.shipment.trailer.length_feet > 0 && (
                            <span className="text-xs text-gray-500">
                              {driver.shipment.trailer.length_feet}ft
                            </span>
                          )}
                        </div>
                      )}
                      {driver.sealNumber && (
                        <p className="text-sm">
                          <span className="font-medium">Seal:</span> {driver.sealNumber}
                        </p>
                      )}
                      {driver.shipment?.metadata?.drop_turned_live && (
                        <div className="mt-1">
                          <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800 font-medium">
                            Drop Turned Live
                          </span>
                          {driver.shipment?.metadata?.drop_turned_live_at && (
                            <p className="text-xs text-gray-500 mt-0.5 ml-0.5">
                              Turned Live: {formatMilitaryDateTime(driver.shipment.metadata.drop_turned_live_at)}
                            </p>
                          )}
                        </div>
                      )}
                      {driver.shipment?.metadata?.appointment_date && (
                        <p className="text-sm">
                          <span className="font-medium">Appointment:</span> {formatMilitaryDateTime(driver.shipment?.metadata?.appointment_date)}
                        </p>
                      )}
                      {driver.shipment?.metadata?.appointment_must_depart_by_date && (
                        <p className="text-sm">
                          <span className="font-medium">Must Depart:</span> {formatMilitaryDateTime(driver.shipment?.metadata?.appointment_must_depart_by_date)}
                        </p>
                      )}
                      {driver.weight && (
                        <p className="text-sm">
                          <span className="font-medium">Weight:</span> {driver.weight}
                        </p>
                      )}
                      <div className="flex justify-between mt-2 text-xs text-gray-500">
                        <span>Checked in: {driver.arrivalTime}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Available Slots Panel */}
          <div className="w-full md:w-2/3 bg-white p-4 shadow relative flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-lg">
                {showUnavailable 
                  ? `Spots (${availableSlots.length} available / ${filteredSlots.length} total)` 
                  : `Available Spots (${availableSlots.length})`}
              </h2>
              <div className="flex gap-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={showUnavailable}
                      onChange={(e) => setShowUnavailable(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors duration-200 flex items-center ${
                      showUnavailable ? 'bg-sky-600' : 'bg-gray-300'
                    }`}>
                      <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                        showUnavailable ? 'translate-x-6' : 'translate-x-1'
                      }`}></div>
                    </div>
                  </div>
                  Show unavailable
                </label>
                <Select
                  options={[
                    { value: 'all', label: 'All Types' },
                    { value: 'yard', label: 'Yard Spots Only' },
                    { value: 'dock', label: 'Dock Doors Only' }
                  ]}
                  placeholder="Filter Slots"
                  className="w-40"
                  defaultValue={{ value: 'dock', label: 'Dock Doors Only' }}
                  onChange={(option: any) => setSlotFilter(option.value)}
                />
                {/* <button 
                  className="bg-sky-600 hover:bg-sky-500 text-white px-3 py-1 rounded text-sm"
                  disabled={availableSlots.length === 0 || unassignedDrivers.length === 0}
                  onClick={() => availableSlots.length > 0 && openAssignModal('slot-to-driver', undefined, availableSlots[0])}
                >
                  Assign First
                </button> */}
              </div>
            </div>
            
            {/* Drag instruction banner */}
            {draggedDriver && (
              <div className="mb-4 bg-blue-100 border border-blue-300 rounded-lg px-4 py-3 text-center">
                <span className="text-blue-700 font-medium">
                  🎯 Dragging "{draggedDriver.name}" - drop on any available yard spot below
                </span>
              </div>
            )}
            
            <div className="max-h-[576px] overflow-y-auto space-y-4">
              {filteredSlots.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {showUnavailable ? 'No spots of this type' : 'No available spots'}
                </div>
              ) : zones.length > 0 ? (
                // Group by zones
                zones.map(zone => {
                  // Filter spots in this zone based on the slot filter and availability toggle
                  const filteredZoneSpots = zone.spots.filter(spot => {
                    const typeMatches = slotFilter === 'all' ? true : spot.type === slotFilter;
                    if (showUnavailable) {
                      return typeMatches; // Show all spots when "show unavailable" is enabled
                    } else {
                      // Only show available spots that are not locked
                      return typeMatches && spot.status === 'available' && !allLockedSlots.has(spot.id);
                    }
                  });
                  
                  // Calculate truly available spots (not locked) for accurate counting
                  // When showUnavailable is false, filteredZoneSpots already excludes locked spots
                  const availableZoneSpots = showUnavailable 
                    ? filteredZoneSpots.filter(spot => spot.status === 'available' && !allLockedSlots.has(spot.id))
                    : filteredZoneSpots; // Already filtered to exclude locked spots
                  
                  // Only show zone if it has spots matching the filter
                  if (filteredZoneSpots.length === 0) return null;
                  
                  return (
                    <div key={zone.id} className="border rounded-lg overflow-hidden">
                      {/* Zone Header */}
                      <div 
                        className={`px-3 py-2 text-white font-semibold text-sm flex items-center justify-between transition-all duration-200 ${
                          dragOverZone === zone.id ? 'shadow-lg brightness-110' : ''
                        }`}
                        style={{ backgroundColor: zone.color }}
                      >
                        <span>{zone.name}</span>
                        <span className="bg-white bg-opacity-20 px-2 py-1 rounded text-xs">
                          {showUnavailable 
                            ? `${filteredZoneSpots.length} spots (${availableZoneSpots.length} available)`
                            : `${filteredZoneSpots.length} spots`
                          }
                        </span>
                      </div>
                      
                      {/* Zone Spots */}
                      <div className={`p-3 transition-all duration-200 ${
                        draggedDriver ? 'bg-blue-50' : 'bg-gray-50'
                      }`}>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                          {filteredZoneSpots.map(slot => {
                            const isLocked = allLockedSlots.has(slot.id);
                            const isAvailable = slot.status === 'available' && !isLocked;
                            const isInUse = slot.status === 'in-use';
                            const isUnavailable = slot.status === 'unavailable';
                            
                            return (
                              <div 
                                key={slot.id} 
                                className={`border rounded p-2 transition-all duration-200 border-l-4 ${
                                  !isAvailable 
                                    ? isUnavailable 
                                      ? 'bg-gray-200 cursor-not-allowed opacity-70 text-gray-500' 
                                      : 'bg-gray-100 cursor-not-allowed opacity-60' 
                                    : dragOverSpot === slot.id 
                                      ? 'bg-blue-200 border-blue-500 shadow-lg scale-110 ring-2 ring-blue-300 cursor-pointer' 
                                      : draggedDriver 
                                        ? 'bg-white hover:bg-blue-100 hover:border-blue-400 hover:shadow-md hover:scale-105 cursor-pointer' 
                                        : 'bg-white hover:bg-gray-100 hover:shadow-sm cursor-pointer'
                                } ${mergeLockSets(taskBasedLockedSlots, lockedSlots).has(slot.id) ? 'relative' : ''}`}
                                style={{ borderLeftColor: isAvailable ? zone.color : '#9CA3AF' }}
                                // onClick={() => isAvailable && openAssignModal('slot-to-driver', undefined, slot)}
                                onDragOver={isAvailable ? handleDragOver : undefined}
                                onDragEnter={isAvailable ? (e) => handleDragEnterSpot(e, slot) : undefined}
                                onDragLeave={isAvailable ? handleDragLeaveSpot : undefined}
                                onDrop={isAvailable ? (e) => handleDropOnSpot(e, slot) : undefined}
                              >
                                {/* Gray overlay for locked slots */}
                                {mergeLockSets(taskBasedLockedSlots, lockedSlots).has(slot.id) && (
                                  <div 
                                    className="absolute inset-0 bg-gray-500 bg-opacity-50 rounded flex items-center justify-center z-20 cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (taskBasedLockedSlots.has(slot.id)) {
                                        const locks = getLocksForSlot(slot.id);
                                        if (locks.length > 0) {
                                          handleShowTaskFromLock(locks[0]);
                                        }
                                      }
                                    }}
                                  >
                                    <div className="bg-white px-2 py-1 rounded text-xs font-medium text-gray-700 shadow">
                                      {taskBasedLockedSlots.has(slot.id) ? 'Active Task' : 'Processing...'}
                                    </div>
                                  </div>
                                )}
                                <div className="text-center">
                                  <span className={`font-bold text-sm ${!isAvailable ? 'text-gray-500' : ''}`}>
                                    {slot.number}
                                  </span>
                                  <div className="flex items-center justify-center mt-1">
                                    <span className={`text-xs px-2 py-1 rounded ${
                                      isAvailable 
                                        ? 'bg-green-100 text-green-800' 
                                        : isInUse 
                                          ? 'bg-red-100 text-red-800' 
                                          : isUnavailable
                                            ? 'bg-gray-100 text-gray-600'
                                            : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {isAvailable ? 'Available' : isInUse ? 'In Use' : 'Unavailable'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                  );
                }).filter(Boolean)
              ) : (
                // Fallback to simple grid if no zones
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filteredSlots.map(slot => {
                    const isLocked = allLockedSlots.has(slot.id);
                    const isAvailable = slot.status === 'available' && !isLocked;
                    const isInUse = slot.status === 'in-use';
                    const isUnavailable = slot.status === 'unavailable';
                    
                    return (
                      <div 
                        key={slot.id} 
                        className={`border rounded p-3 transition-all duration-200 ${
                          isAvailable 
                            ? 'bg-green-50 border-green-200 cursor-pointer hover:bg-green-100' 
                            : isUnavailable
                              ? 'bg-gray-200 border-gray-300 cursor-not-allowed opacity-70 text-gray-500'
                              : 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-60'
                        } ${mergeLockSets(taskBasedLockedSlots, lockedSlots).has(slot.id) ? 'relative' : ''}`}
                        onClick={() => isAvailable && openAssignModal('slot-to-driver', undefined, slot)}
                      >
                        {/* Gray overlay for locked slots */}
                        {mergeLockSets(taskBasedLockedSlots, lockedSlots).has(slot.id) && (
                          <div 
                            className="absolute inset-0 bg-gray-500 bg-opacity-50 rounded flex items-center justify-center z-20 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (taskBasedLockedSlots.has(slot.id)) {
                                const locks = getLocksForSlot(slot.id);
                                if (locks.length > 0) {
                                  handleShowTaskFromLock(locks[0]);
                                }
                              }
                            }}
                          >
                            <div className="bg-white px-2 py-1 rounded text-xs font-medium text-gray-700 shadow">
                              {taskBasedLockedSlots.has(slot.id) ? 'Active Task' : 'Processing...'}
                            </div>
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <span className={`font-bold ${!isAvailable ? 'text-gray-500' : ''}`}>
                            Spot {slot.number}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            isAvailable 
                              ? 'bg-green-100 text-green-800' 
                              : isInUse 
                                ? 'bg-red-100 text-red-800' 
                                : isUnavailable
                                  ? 'bg-gray-100 text-gray-600'
                                  : 'bg-gray-100 text-gray-600'
                          }`}>
                            {isAvailable ? 'Available' : isInUse ? 'In Use' : 'Unavailable'}
                          </span>
                        </div>
                        <p className={`text-sm mt-1 ${!isAvailable ? 'text-gray-500' : 'text-gray-600'}`}>
                          {slot.type === 'yard' ? 'Yard Spot' : 'Dock Door'}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tabValue === 1 && (
        <PrecheckedDrivers
          facilityId={selectedFacility?.id || null}
          sinceHours={sinceHours}
        />
      )}
      
      {/* Assignment Modal */}
      {showAssignModal && (
        <Modal
          open={showAssignModal}
          onClose={isAssigning ? () => {} : closeAssignModal}
          className="flex justify-center items-center"
        >
          <div className="w-96 bg-white border p-5 max-h-[80vh] overflow-y-auto relative flex flex-col">
            {/* Loading overlay */}
            {isAssigning && (
              <div className="absolute inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 rounded">
                <div className="bg-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-3">
                  <CircularProgress size={20} />
                  <span className="text-gray-700 font-medium">Creating assignment...</span>
                </div>
              </div>
            )}
            <Close
              className={`absolute top-4 right-4 ${isAssigning ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer hover:text-slate-400'}`}
              onClick={isAssigning ? () => {} : closeAssignModal}
            />
            <h1 className="text-xl mb-5">
              {assignmentType === 'driver-to-slot' 
                ? 'Assign Driver to Location' 
                : 'Assign Location to Driver'}
            </h1>
            
            {/* Driver Selection */}
            <div className="mb-4">
              <h2 className="font-bold mb-2">Driver</h2>
              {selectedDriver ? (
                <div className="p-3 border rounded bg-gray-50">
                  <div className="flex justify-between">
                    <span className="font-bold">{selectedDriver.name}</span>
                    <span className={`text-xs px-2 py-1 rounded ${getDriverTypeColor(selectedDriver.type)}`}>
                      {selectedDriver.type.charAt(0).toUpperCase() + selectedDriver.type.slice(1)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Ref #: {selectedDriver.loadNumber}</p>
                  {selectedDriver.shipment?.trailer && (
                    <div className="text-sm mt-1">
                      <p>
                        <span className="font-medium">Trailer:</span> {selectedDriver.shipment.trailer.trailer_number || 'N/A'}
                        {selectedDriver.shipment.trailer.trailer_type && (
                          <span className="text-gray-500 ml-1">({selectedDriver.shipment.trailer.trailer_type})</span>
                        )}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {selectedDriver.shipment.trailer.refrigerated && (
                          <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                            Refrigerated
                          </span>
                        )}
                                                 {selectedDriver.shipment.trailer.length_feet && selectedDriver.shipment.trailer.length_feet > 0 && (
                           <span className="text-xs text-gray-500">
                             {selectedDriver.shipment.trailer.length_feet}ft
                           </span>
                         )}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Created: {selectedDriver.arrivalTime}
                  </p>
                </div>
              ) : (
                <Select
                  options={unassignedDrivers.map(driver => ({
                    value: driver.id,
                    label: `${driver.name} - ${driver.loadNumber || 'No Load'}`
                  }))}
                  placeholder="Select Driver"
                  onChange={(option: any) => {
                    const driver = drivers.find(d => d.id === option.value);
                    if (driver) setSelectedDriver(driver);
                  }}
                />
              )}
            </div>
            
            {/* Slot Selection */}
            <div className="mb-4">
              <h2 className="font-bold mb-2">Location</h2>
              {selectedSlot ? (
                <div className="p-3 border rounded bg-green-50">
                  <div className="flex justify-between items-center">
                    <span className="font-bold">{selectedSlot.type === 'dock' ? 'Dock' : 'Spot'} {selectedSlot.number}</span>
                    <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800">
                      Available
                    </span>
                  </div>
                  {selectedSlot.zoneName && (
                    <div className="flex items-center mt-2 space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: zones.find(z => z.id === selectedSlot.zoneId)?.color }}
                      ></div>
                      <span className="text-sm text-gray-600">Zone: {selectedSlot.zoneName}</span>
                    </div>
                  )}
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedSlot.type === 'dock' ? 'Dock Door' : 'Yard Spot'}
                  </p>
                </div>
              ) : (
                <Select
                  options={zones.length > 0 ? 
                    zones.flatMap(zone => 
                      zone.spots
                        .filter(slot => slotFilter === 'all' ? true : slot.type === slotFilter)
                        .filter(slot => slot.status === 'available') // This already excludes unavailable
                        .filter(slot => !allLockedSlots.has(slot.id)) // Exclude locked spots
                        .map(slot => ({
                          value: slot.id,
                          label: `${slot.number} (${zone.name})`,
                          zone: zone.name,
                          color: zone.color
                        }))
                    ).filter(option => option !== undefined) :
                    availableSlots
                      .filter(slot => slotFilter === 'all' ? true : slot.type === slotFilter)
                      .filter(slot => slot.status === 'available') // Ensure unavailable slots are excluded here too
                      .filter(slot => !allLockedSlots.has(slot.id)) // Exclude locked spots
                      .map(slot => ({
                        value: slot.id,
                        label: `${slot.type === 'dock' ? 'Dock' : 'Spot'} ${slot.number}`
                      }))
                  }
                  placeholder="Select Location"
                  onChange={(option: any) => {
                    const slot = slots.find(s => s.id === option.value);
                    if (slot) setSelectedSlot(slot);
                  }}
                  formatOptionLabel={(option: any) => (
                    <div className="flex items-center space-x-2">
                      {option.color && (
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: option.color }}
                        ></div>
                      )}
                      <span>{option.label}</span>
                    </div>
                  )}
                />
              )}
            </div>
            
           
            
            {/* Notes */}
            <div className="mb-4">
              <h2 className="font-bold mb-2">Notes (Optional)</h2>
              <textarea
                className="w-full border rounded p-2 h-20"
                placeholder="Add any notes about this assignment"
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
              />
            </div>
            
            {/* Auto-complete checkbox */}
            <div className="mb-4">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoComplete}
                    onChange={(e) => setAutoComplete(e.target.checked)}
                    disabled={isAssigning}
                  />
                }
                label="Auto-complete task (task will be marked as completed immediately)"
              />
            </div>
            
            <div className="mt-auto pt-3 border-t flex justify-between">
              <button
                className={`p-2 rounded ${isAssigning ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'}`}
                onClick={isAssigning ? () => {} : closeAssignModal}
                disabled={isAssigning}
              >
                Cancel
              </button>
              <button
                className={twMerge(
                  'p-2 rounded flex items-center',
                  isAssigning 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : (!selectedDriver || !selectedSlot) 
                      ? 'bg-gray-300 hover:bg-gray-300 cursor-not-allowed text-gray-500'
                      : 'bg-sky-600 hover:bg-sky-500 text-white'
                )}
                onClick={isAssigning ? () => {} : handleAssignment}
                disabled={!selectedDriver || !selectedSlot || isAssigning}
              >
                {isAssigning && <CircularProgress size={16} className="mr-2" />}
                {isAssigning ? 'Creating...' : 'Confirm Assignment'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Success Modal */}
      {showSuccessModal && createdTask && (
        <Modal
          open={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false);
            setCreatedTask(null);
          }}
          className="flex justify-center items-center"
        >
          <div className="w-[500px] bg-white border p-6 max-h-[90vh] overflow-y-auto relative">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
              onClick={() => {
                setShowSuccessModal(false);
                setCreatedTask(null);
              }}
            />
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="text-green-600 text-2xl" />
              </div>
              <h1 className="text-xl font-bold text-green-700 mb-2">Movement Task Created Successfully!</h1>
              <p className="text-gray-600">The driver has been assigned and a movement task has been created.</p>
            </div>
            
            {/* Task Details */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
              <h2 className="font-semibold mb-3 text-gray-800">Task Details</h2>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Task ID:</span>
                  <span className="text-gray-800 font-mono text-sm">{createdTask.uuid}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Task Type:</span>
                  <span className="text-gray-800">{createdTask.task_type?.type || 'Movement Task'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Status:</span>
                  <span className="text-gray-800">{createdTask.task_status?.status || 'Created'}</span>
                </div>
                {createdTask.from_location && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">From:</span>
                    <span className="text-gray-800">{createdTask.from_location}</span>
                  </div>
                )}
                {createdTask.to_location && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">To:</span>
                    <span className="text-gray-800">{createdTask.to_location}</span>
                  </div>
                )}
                {createdTask.trailer?.trailer_number && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Trailer:</span>
                    <span className="text-gray-800">{createdTask.trailer.trailer_number}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-between pt-4 border-t">
              <button 
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 p-2 rounded"
                onClick={() => {
                  setShowSuccessModal(false);
                  setCreatedTask(null);
                }}
              >
                Close
              </button>
              <button 
                className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded flex items-center"
                onClick={() => {
                  // Navigate to the task page
                  window.open(`/tasks/${createdTask.uuid}`, '_blank');
                }}
              >
                <ArrowForward className="mr-2" fontSize="small" />
                View Task Details
              </button>
            </div>
          </div>
        </Modal>
      )}

      <Snackbar 
        open={showAutoAssignResult} 
        autoHideDuration={6000} 
        onClose={() => setShowAutoAssignResult(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowAutoAssignResult(false)} 
          severity={autoAssignStatus.error > 0 ? "warning" : "success"} 
          sx={{ width: '100%' }}
        >
          Auto-assignment complete: {autoAssignStatus.success} successful, {autoAssignStatus.error} failed
        </Alert>
      </Snackbar>

      {/* Task Details Modal */}
      <TaskDetailsModal
        open={showTaskDetailsModal}
        onClose={() => {
          setShowTaskDetailsModal(false);
          setSelectedTaskForDetails(null);
        }}
        task={selectedTaskForDetails}
        onCancelTask={handleCancelTask}
        onCompleteTask={handleCompleteTask}
        onCompleteMovementTask={handleCompleteMovementTask}
      />
    </div>
  );
};

export default DriverSlotAssignment; 